import { MouseEvent, useState } from 'react';
import { bulkCancelRides } from '@rideiq/api/services/Rides';
import { useLoaderStore } from '@rideiq/shared/stores/loaderStore';
import { useToastStore } from '@rideiq/shared/stores/toastStore';

type RideIds = number[];

interface IUseBulkCancelRides {
  selectedRideIds: RideIds;
  handleCheckboxSelection: (event: MouseEvent, id: number, rideIds: RideIds) => void;
  handleCancelRides: ({ cancellationReasonKey }) => void;
  cancelRidesResponse: string;
  reset: () => void;
}

export const useBulkCancelRides = (): IUseBulkCancelRides => {
  const [selectedRideIds, setSelectedRideIds] = useState<RideIds>([]);
  const [cancelRidesResponse, setCancelRidesResponse] = useState<string>('');
  const [lastSelectedId, setLastSelectedId] = useState<Nullable<number>>();
  const { showLoader, hideLoader } = useLoaderStore();
  const { addToast } = useToastStore();

  const handleCheckboxSelection = (event: MouseEvent, id: number, rideIds: RideIds) => {
    event.stopPropagation();
    let additionalIdsToCheck: RideIds = [];

    if (event.nativeEvent.shiftKey && lastSelectedId) {
      let shouldAddToSelectedIds = false;

      additionalIdsToCheck = rideIds
        .map((rideId) => {
          if (rideId === id) {
            shouldAddToSelectedIds = false;

            return null;
          }
          if (shouldAddToSelectedIds) {
            return rideId;
          }
          if (rideId === lastSelectedId) {
            shouldAddToSelectedIds = true;

            return rideId;
          }

          return null;
        })
        .filter(Boolean) as RideIds;
    }

    if (selectedRideIds.indexOf(id) !== -1) {
      setSelectedRideIds(selectedRideIds.filter((_, rideIndex) => rideIndex !== selectedRideIds.indexOf(id)));
    } else {
      setLastSelectedId(id);
      setSelectedRideIds(Array.from(new Set([...selectedRideIds, ...additionalIdsToCheck, id])));
    }
  };

  const handleCancelRides = async ({ cancellationReasonKey }) => {
    if (selectedRideIds.length) {
      showLoader();
      const cancelResponse = await bulkCancelRides({
        cancel: { ids: selectedRideIds, cancellation_reason_key: cancellationReasonKey },
      });

      if (cancelResponse.type === 'error') {
        cancelResponse.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      } else {
        setCancelRidesResponse(cancelResponse.data.message);
      }
      hideLoader();
    }
  };

  const reset = () => {
    setSelectedRideIds([]);
    setCancelRidesResponse('');
  };

  return {
    selectedRideIds,
    handleCheckboxSelection,
    handleCancelRides,
    cancelRidesResponse,
    reset,
  };
};
